import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Phone,
  Upload,
  ShieldCheck,
  CheckCircle2,
  Camera,
  Sparkles,
  ClipboardList,
  Star,
  MapPin,
  Home as HomeIcon,
} from 'lucide-react';
import LandingNav, { PHONE_DISPLAY, PHONE_TEL } from '@/components/LandingNav';
import LandingFooter from '@/components/LandingFooter';
import QuoteVerifyForm from '@/components/QuoteVerifyForm';
import OpeningCatalog from '@/components/OpeningCatalog';
import SEO from '@/components/SEO';

const HERO_IMAGE =
  'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/p1dw6cbv_WhatsApp%20Image%202026-04-17%20at%2012.19.27%20PM.jpeg';

const ECO_LOGO =
  'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/w9jekft2_eco_window_systems_logo%20%281%29.jpg';
const MR_GLASS_LOGO =
  'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/1edhksrg_Mr-Glass-Logo.png';
const ES_LOGO =
  'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/dt288m9t_ESWindow-Logo_Web.png';

const trackPixel = (event, data = {}) => {
  if (window.fbq) {
    if (['Lead', 'InitiateCheckout', 'CompleteRegistration'].includes(event)) {
      window.fbq('track', event, data);
    } else {
      window.fbq('trackCustom', event, data);
    }
  }
};
const trackGA = (event, data = {}) => {
  if (window.gtag) window.gtag('event', event, data);
};

export default function GetQuoteLanding() {
  const formRef = useRef(null);

  useEffect(() => {
    trackPixel('ViewContent', { content_name: 'Get Quote Landing' });
    trackGA('view_landing_get_quote', {
      event_category: 'paid_traffic',
      event_label: 'Get Quote Landing',
    });
  }, []);

  const scrollToForm = (location) => {
    trackGA('cta_click_get_quote', {
      event_category: 'engagement',
      event_label: location,
    });
    document.querySelector('[data-testid="verify-section"]')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <SEO
        title="Get Your Impact Window Quote — Upload Photos, No Appointment"
        description="Upload photos of your windows and doors and receive Good/Better/Premium installed pricing within 24 hours. No in-home sales appointment required."
        path="/get-quote"
      />
      <LandingNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--brand-ocean-100)]/40 via-white to-white pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-10 md:pt-16 md:pb-14">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Badge
                variant="outline"
                className="rounded-full px-3 py-1 text-[11px] uppercase tracking-wider border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] bg-white"
              >
                South Florida • Impact Windows & Doors
              </Badge>
              <h1
                className="mt-4 font-heading text-4xl sm:text-5xl md:text-6xl font-semibold leading-[1.05] tracking-tight text-[var(--brand-slate-900)]"
                data-testid="hero-headline"
              >
                Upload Your Openings.{' '}
                <span className="text-[var(--brand-ocean-700)]">Get Installed Pricing.</span>
              </h1>
              <p
                className="mt-5 text-lg sm:text-xl text-[var(--brand-slate-600)] max-w-xl"
                data-testid="hero-subheadline"
              >
                Photo-based quotes with Good / Better / Premium installed pricing — no in-home sales appointment.
              </p>

              <ul className="mt-7 space-y-3 max-w-xl" data-testid="hero-bullets">
                {['No in-home appointment', 'Final measurements confirmed before ordering', 'Permits & install included'].map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-[var(--brand-slate-800)]"
                  >
                    <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--brand-ocean-100)] text-[var(--brand-ocean-700)]">
                      <CheckCircle2 className="h-4 w-4" />
                    </span>
                    <span className="text-base sm:text-[17px]">{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  onClick={() => scrollToForm('hero')}
                  data-testid="hero-cta-upload-photos"
                  className="h-14 px-7 rounded-xl text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-95 shadow-md hover:shadow-lg"
                >
                  <Upload className="mr-2 h-5 w-5" />
                  Get My Price
                </Button>
                <a
                  href={PHONE_TEL}
                  className="inline-flex items-center justify-center h-14 px-6 rounded-xl text-base font-semibold border-2 border-[hsl(var(--border))] text-[var(--brand-slate-800)] hover:bg-[hsl(var(--secondary))]"
                  onClick={() =>
                    trackGA('phone_call_click', {
                      event_category: 'engagement',
                      event_label: 'hero_call_us',
                    })
                  }
                  data-testid="hero-call-cta"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Or call {PHONE_DISPLAY}
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-xl border border-[hsl(var(--border))] bg-white">
                <img
                  src={HERO_IMAGE}
                  alt="Impact windows installed on a South Florida home"
                  loading="eager"
                  className="w-full h-[360px] sm:h-[440px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 flex items-center gap-3 shadow-md">
                  <div className="flex -space-x-1.5">
                    {[1, 2, 3].map((i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-[var(--brand-slate-700)]">
                    <span className="font-semibold">200+ South Florida homes</span>{' '}
                    quoted without a sales visit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section
        className="border-y border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40"
        data-testid="trust-strip"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
            {[
              { icon: CheckCircle2, text: 'Real projects from $15K–$40K+' },
              { icon: ShieldCheck, text: 'Condo & HOA compliant installations' },
              { icon: MapPin, text: 'Serving Miami-Dade, Broward & Palm Beach' },
            ].map((item) => (
              <div
                key={item.text}
                className="flex items-center gap-2.5 text-sm sm:text-[15px] text-[var(--brand-slate-700)] justify-start sm:justify-center"
              >
                <item.icon className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0" />
                <span className="font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
              How it works
            </p>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)]">
              Three simple steps.
            </h2>
          </div>
          <div className="mt-8 sm:mt-10 grid md:grid-cols-3 gap-4 sm:gap-5">
            {[
              { n: '01', icon: Camera, title: 'Upload Photos', desc: 'Take a few pictures of your windows and doors.' },
              { n: '02', icon: Sparkles, title: 'We Build Your Options', desc: 'We create Good, Better, and Premium pricing options.' },
              { n: '03', icon: ClipboardList, title: 'Receive Your Quote', desc: 'Get your pricing via text — no appointment needed.' },
            ].map((step) => (
              <Card
                key={step.n}
                className="rounded-2xl border-2 hover:border-[var(--brand-ocean-600)] transition-colors"
                data-testid={`how-it-works-step-${step.n}`}
              >
                <CardContent className="p-5 sm:p-6">
                  <div className="flex items-center justify-between">
                    <span className="text-4xl font-heading font-semibold text-[var(--brand-ocean-700)]/40 leading-none">
                      {step.n}
                    </span>
                    <div className="h-11 w-11 rounded-xl bg-[var(--brand-ocean-100)] flex items-center justify-center">
                      <step.icon className="h-5 w-5 text-[var(--brand-ocean-700)]" />
                    </div>
                  </div>
                  <h3 className="mt-4 font-heading font-semibold text-xl text-[var(--brand-slate-900)]">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm text-[var(--brand-slate-600)] leading-relaxed">
                    {step.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Good / Better / Premium */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/40" data-testid="tiers-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
              Your options
            </p>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)]">
              Compare Your Options — Not Just One Price
            </h2>
          </div>
          <div className="mt-8 sm:mt-10 grid md:grid-cols-3 gap-4 sm:gap-5">
            {[
              { id: 'good', tier: 'Good', brand: 'ECO Windows', desc: 'Affordable, code-compliant', logo: ECO_LOGO },
              { id: 'better', tier: 'Better', brand: 'Mr. Glass', desc: 'Stronger frames, better performance', logo: MR_GLASS_LOGO, featured: true },
              { id: 'premium', tier: 'Premium', brand: 'ES Windows', desc: 'Top-tier design and durability', logo: ES_LOGO },
            ].map((t) => (
              <Card
                key={t.id}
                className={`rounded-2xl ${t.featured ? 'border-2 border-[var(--brand-ocean-600)] bg-white shadow-sm' : 'border border-[hsl(var(--border))] bg-white'}`}
                data-testid={`tier-card-${t.id}`}
              >
                <CardContent className="p-5 sm:p-6 text-center flex flex-col items-center">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
                    {t.tier}
                  </p>
                  <div className="mt-3 h-14 sm:h-16 flex items-center justify-center w-full">
                    <img
                      src={t.logo}
                      alt={`${t.brand} logo`}
                      loading="lazy"
                      draggable={false}
                      className="max-h-full max-w-[160px] object-contain select-none pointer-events-none"
                      data-testid={`tier-logo-${t.id}`}
                    />
                  </div>
                  <h3 className="mt-3 font-heading text-lg sm:text-xl font-semibold text-[var(--brand-slate-900)]">
                    {t.brand}
                  </h3>
                  <p className="mt-1.5 text-sm text-[var(--brand-slate-600)] leading-relaxed">
                    {t.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p
            className="mt-7 text-center text-sm sm:text-base text-[var(--brand-slate-600)] max-w-2xl mx-auto"
            data-testid="tiers-caption"
          >
            We show you all 3 options so you can choose what works best for your home and budget.
          </p>
        </div>
      </section>

      {/* Form */}
      <div ref={formRef}>
        <QuoteVerifyForm sourceLabel="get_quote" />
      </div>

      {/* Opening Catalog — placed BELOW the verify form so the SMS form remains the primary first interaction */}
      <OpeningCatalog src="get_quote_catalog" variant="compact" />

      {/* Real Proof — plain text */}
      <section className="py-14 sm:py-20" data-testid="proof-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
            Real proof
          </p>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)]">
            Real Projects, Real Pricing
          </h2>
          <div className="mt-7 sm:mt-9 divide-y divide-[hsl(var(--border))]">
            {[
              { id: 'kendall', title: 'Kendall Full Home', stat: '~$28,400', desc: 'Complete impact window and door replacement using ES Windows' },
              { id: 'north-miami', title: 'North Miami Condo', stat: '~$25,000', desc: 'HOA-compliant installation with coastal-grade materials (Mr. Glass)' },
            ].map((cs) => (
              <div key={cs.id} className="py-5 sm:py-6 first:pt-0 last:pb-0" data-testid={`proof-item-${cs.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <h3 className="font-heading text-lg sm:text-xl font-semibold text-[var(--brand-slate-900)]">{cs.title}</h3>
                  <span className="font-heading text-lg sm:text-xl font-semibold text-[var(--brand-ocean-700)] whitespace-nowrap">— {cs.stat}</span>
                </div>
                <p className="mt-1.5 text-sm sm:text-[15px] text-[var(--brand-slate-600)] leading-relaxed">{cs.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Expectations */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/40" data-testid="pricing-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
            Transparent pricing
          </p>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)]">
            What Do Impact Windows Cost?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[var(--brand-slate-700)] leading-relaxed">
            Most projects range from{' '}
            <span className="font-semibold text-[var(--brand-ocean-700)]">$15,000 to $40,000+</span>{' '}
            depending on home size, product selection, and installation requirements.
          </p>
          <p className="mt-3 text-base text-[var(--brand-slate-600)] leading-relaxed">
            We provide Good, Better, and Premium options so you can choose what works best for your home and budget.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 sm:py-20" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="rounded-3xl border-2 border-[var(--brand-ocean-600)] bg-gradient-to-br from-[var(--brand-ocean-100)]/40 via-white to-white p-8 sm:p-12 text-center shadow-sm">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--brand-ocean-100)] mb-5">
              <HomeIcon className="h-7 w-7 text-[var(--brand-ocean-700)]" />
            </div>
            <p className="text-base sm:text-lg text-[var(--brand-slate-700)] max-w-xl mx-auto leading-relaxed">
              Whether you're ready now or just exploring, we’ll help you understand
              exactly what your project could cost.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                onClick={() => scrollToForm('final_cta')}
                data-testid="final-cta-button"
                className="h-14 px-8 rounded-xl text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-md hover:shadow-lg"
              >
                <Upload className="mr-2 h-5 w-5" />
                Get My Price
              </Button>
              <a
                href={PHONE_TEL}
                onClick={() =>
                  trackGA('phone_call_click', {
                    event_category: 'engagement',
                    event_label: 'final_cta_call_us',
                  })
                }
                className="inline-flex items-center justify-center h-14 px-6 rounded-xl text-base font-semibold border-2 border-[var(--brand-slate-200)] text-[var(--brand-slate-800)] hover:bg-white"
                data-testid="final-cta-call"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
